import React, { useState } from 'react';
import { Currency, Alert, AlertCondition } from '../types';
import PencilIcon from './icons/PencilIcon';
import TrashIcon from './icons/TrashIcon';

interface AlertsProps {
  activeAlerts: Alert[];
  onSetAlert: (alert: Omit<Alert, 'id'>) => void;
  onRemoveAlert: (id: string) => void;
  onUpdateAlert: (alert: Alert) => void;
}

const Alerts: React.FC<AlertsProps> = ({ activeAlerts, onSetAlert, onRemoveAlert, onUpdateAlert }) => {
  const [targetPrice, setTargetPrice] = useState('');
  const [currency, setCurrency] = useState<Currency>(Currency.USD);
  const [alertCondition, setAlertCondition] = useState<AlertCondition>('above');
  const [notificationError, setNotificationError] = useState('');
  const [editingAlertId, setEditingAlertId] = useState<string | null>(null);

  const isEditing = editingAlertId !== null;

  const handlePriceInputChange = (value: string) => {
    if (value === '') {
      setTargetPrice('');
      return;
    }
    const sanitized = value.replace(/[^0-9.]/g, '');
    const parts = sanitized.split('.');
    if (parts.length > 2) return;
    const integerPart = parts[0];
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    let finalValue = formattedIntegerPart;
    if (parts.length > 1) {
      finalValue += '.' + parts[1].slice(0, 2);
    }
    setTargetPrice(finalValue);
  };

  const resetForm = () => {
    setTargetPrice('');
    setEditingAlertId(null);
    setCurrency(Currency.USD);
    setAlertCondition('above');
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetPrice) return;
    setNotificationError('');

    // Check for notification permission
    if (Notification.permission === 'denied') {
      setNotificationError('Notifications are blocked. Please enable them in your browser settings to use alerts.');
      return;
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setNotificationError('Notification permission is required to set alerts.');
        return;
      }
    }

    const numericPrice = parseFloat(targetPrice.replace(/,/g, ''));
    if (isNaN(numericPrice)) return;
    
    if (isEditing) {
        onUpdateAlert({
            id: editingAlertId,
            targetPrice: numericPrice,
            currency,
            condition: alertCondition,
        });
    } else {
        onSetAlert({
          targetPrice: numericPrice,
          currency,
          condition: alertCondition,
        });
    }
    resetForm();
  };
  
  const handleEditClick = (alert: Alert) => {
    setEditingAlertId(alert.id);
    setTargetPrice(alert.targetPrice.toLocaleString('en-US', { useGrouping: true, minimumFractionDigits: 0, maximumFractionDigits: 2 }));
    setCurrency(alert.currency);
    setAlertCondition(alert.condition);
    // Scroll form into view for better UX on mobile
    const formEl = document.getElementById('alert-form-container');
    formEl?.scrollIntoView({ behavior: 'smooth' });
  };

  const getCurrencySymbol = (c: Currency) => (c === Currency.USD ? '$' : '€');

  return (
    <div id="alert-form-container" className="bg-navy-blue p-6 rounded-lg shadow-xl mb-8 scroll-mt-24">
      <h2 className="text-2xl font-bold mb-4 text-off-white">{isEditing ? 'Update Browser Price Alert' : 'Set Browser Price Alert'}</h2>
      {!isEditing && <p className="text-sm text-light-blue mb-4 -mt-2">Get a notification when the price hits your target. Alerts work only while this page is open.</p>}
      
      <form onSubmit={handleFormSubmit} className="flex flex-col md:flex-row items-center gap-4 mb-6">
        <div className="flex-grow w-full">
          <label htmlFor="price-alert" className="sr-only">Price Alert</label>
          <input
            id="price-alert"
            type="text"
            inputMode="decimal"
            value={targetPrice}
            onChange={(e) => handlePriceInputChange(e.target.value)}
            placeholder="Enter target price"
            className="w-full bg-dark-blue border border-steel-blue rounded-lg p-2 text-off-white focus:ring-btc-orange focus:border-btc-orange"
            required
          />
        </div>
        <div className="flex rounded-lg bg-dark-blue p-1">
          <button type="button" onClick={() => setAlertCondition('above')} className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${alertCondition === 'above' ? 'bg-btc-orange text-dark-blue' : 'text-light-blue hover:bg-steel-blue/50'}`}>Above</button>
          <button type="button" onClick={() => setAlertCondition('below')} className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${alertCondition === 'below' ? 'bg-btc-orange text-dark-blue' : 'text-light-blue hover:bg-steel-blue/50'}`}>Below</button>
        </div>
        <div className="flex rounded-lg bg-dark-blue p-1">
          <button type="button" onClick={() => setCurrency(Currency.USD)} className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${currency === Currency.USD ? 'bg-btc-orange text-dark-blue' : 'text-light-blue hover:bg-steel-blue/50'}`}>USD</button>
          <button type="button" onClick={() => setCurrency(Currency.EUR)} className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${currency === Currency.EUR ? 'bg-btc-orange text-dark-blue' : 'text-light-blue hover:bg-steel-blue/50'}`}>EUR</button>
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          {isEditing && (
             <button type="button" onClick={resetForm} className="w-full md:w-auto bg-steel-blue text-off-white font-bold py-2 px-6 rounded-lg hover:bg-light-blue transition-colors duration-300">
               Cancel
             </button>
          )}
          <button type="submit" className="w-full md:w-auto bg-btc-orange text-dark-blue font-bold py-2 px-6 rounded-lg hover:bg-orange-400 transition-colors duration-300">
            {isEditing ? 'Update Alert' : 'Set Alert'}
          </button>
        </div>
      </form>
      
      {notificationError && (
        <p className="text-center text-red-400 bg-red-900/50 p-2 rounded-lg mb-4">{notificationError}</p>
      )}

      <div>
        <h3 className="text-lg font-semibold text-off-white border-b border-steel-blue pb-2 mb-4">Active Alerts</h3>
        {activeAlerts.length > 0 ? (
          <ul className="space-y-3">
            {activeAlerts.map(alert => (
              <li key={alert.id} className="flex items-center justify-between bg-dark-blue p-3 rounded-lg">
                <p className="text-light-blue">
                  Alert when BTC is <span className="font-bold text-off-white">{alert.condition} {getCurrencySymbol(alert.currency)}{alert.targetPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </p>
                <div className="flex items-center space-x-3">
                   <button 
                    onClick={() => handleEditClick(alert)} 
                    className="text-light-blue hover:text-btc-orange transition-colors"
                    aria-label="Edit alert"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={() => onRemoveAlert(alert.id)} 
                    className="text-light-blue hover:text-red-400 transition-colors"
                    aria-label="Remove alert"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-center text-steel-blue p-4 bg-dark-blue rounded-lg">You have no active alerts.</p>
        )}
      </div>
    </div>
  );
};

export default Alerts;